import { useCallback, useEffect, useRef, useState } from "react";
import api, { fmtINR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Stethoscope, RefreshCw, Receipt } from "lucide-react";

const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "");

export const useAdvisedTests = () => {
  const [date, setDate] = useState(today());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const latest = useRef(date);
  const load = useCallback(async () => {
    setLoading(true);
    const reqDate = date;
    try {
      const rows = (await api.get("/advised-tests", { params: { date: reqDate } })).data || [];
      setRows((prev) => (latest.current === reqDate ? rows : prev));
    } catch { toast.error("Failed to load advised tests"); }
    setLoading(false);
  }, [date]);
  latest.current = date;
  useEffect(() => { load(); }, [load]);
  return { date, setDate, rows, loading, load };
};

export function AdvisedTestsQueue({ q, onBill }) {
  return (
    <div className="bg-white border" data-testid="advised-queue">
      <div className="flex items-center justify-between flex-wrap gap-2 px-4 py-3 border-b">
        <div className="flex items-center gap-2 text-sm font-semibold"><Stethoscope className="w-4 h-4 text-blue-700" /> Tests advised by doctors · not yet billed</div>
        <div className="flex items-center gap-2">
          <Input type="date" value={q.date} onChange={(e) => setDateSafe(q, e.target.value)} className="h-8 w-40" data-testid="advised-date" />
          <Button size="sm" variant="outline" onClick={q.load} className="h-8 rounded-sm" data-testid="advised-refresh"><RefreshCw className={`w-3.5 h-3.5 ${q.loading ? "animate-spin" : ""}`} /></Button>
        </div>
      </div>
      {q.rows.length === 0 ? (
        <div className="p-8 text-center text-sm text-slate-500" data-testid="advised-empty">{q.loading ? "Loading…" : "No pending advised tests for this day."}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase text-slate-500"><tr>
              {["Time", "Patient", "Doctor · OPD", "Advised tests", "Est. amount", ""].map((h) => <th key={h} className="text-left px-3 py-2">{h}</th>)}
            </tr></thead>
            <tbody>{q.rows.map((r) => <AdvisedRow key={r.opd_id} r={r} onBill={onBill} />)}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const setDateSafe = (q, v) => { if (v) q.setDate(v); };

function AdvisedRow({ r, onBill }) {
  return (
    <tr className="border-t align-top hover:bg-blue-50/40" data-testid={`advised-row-${r.opd_no}`}>
      <td className="px-3 py-2 font-mono text-xs">{fmtTime(r.created_at)}</td>
      <td className="px-3 py-2">
        <div className="font-medium">{r.patient.name}</div>
        <div className="text-[11px] text-slate-500 font-mono">{r.patient.uhid} · {r.patient.mobile}</div>
      </td>
      <td className="px-3 py-2 text-xs">{r.doctor_name}<div className="font-mono text-slate-500">{r.opd_no}</div></td>
      <td className="px-3 py-2">
        <div className="flex flex-wrap gap-1">
          {r.tests.map((t) => (
            <span key={t.name} className="text-[11px] border border-slate-300 rounded-sm px-1.5 py-0.5">
              {t.name}{t.body_part ? ` — ${t.body_part}` : ""}{t.quantity > 1 ? ` ×${t.quantity}` : ""}
            </span>
          ))}
        </div>
      </td>
      <td className="px-3 py-2 font-mono">{fmtINR(r.total)}</td>
      <td className="px-3 py-2 text-right">
        <Button size="sm" onClick={() => onBill(r)} className="h-8 rounded-sm bg-blue-700 hover:bg-blue-800" data-testid={`advised-bill-${r.opd_no}`}>
          <Receipt className="w-3.5 h-3.5 mr-1" /> Bill
        </Button>
      </td>
    </tr>
  );
}
